/**
 * Identifiers and reference codes.
 *
 * Deterministic and readable. `emp_0042` in a URL tells you what you are
 * looking at; a UUID does not, and neither survives being read aloud on a
 * support call — which HR teams do constantly.
 *
 * Counters live in memory and are seeded from the highest id already stored, so
 * a reload cannot hand out an id that already exists.
 */

const counters = new Map<string, number>();

export function nextId(prefix: string): string {
  const next = (counters.get(prefix) ?? 0) + 1;
  counters.set(prefix, next);
  return `${prefix}_${String(next).padStart(4, '0')}`;
}

/** Seed a counter above everything already persisted. */
export function primeCounter(prefix: string, existingIds: string[]): void {
  let highest = counters.get(prefix) ?? 0;
  for (const id of existingIds) {
    if (!id.startsWith(`${prefix}_`)) continue;
    const value = Number.parseInt(id.slice(prefix.length + 1), 10);
    if (Number.isFinite(value) && value > highest) highest = value;
  }
  counters.set(prefix, highest);
}

/** Employee-visible reference codes: `LV-2026-0164`. */
export function referenceCode(prefix: string, year: number, sequence: number): string {
  return `${prefix}-${year}-${String(sequence).padStart(4, '0')}`;
}

const sequences = new Map<string, number>();

export function nextReference(prefix: string, year: number): string {
  const key = `${prefix}-${year}`;
  const next = (sequences.get(key) ?? 0) + 1;
  sequences.set(key, next);
  return referenceCode(prefix, year, next);
}

export function primeReference(prefix: string, year: number, codes: string[]): void {
  const key = `${prefix}-${year}`;
  let highest = sequences.get(key) ?? 0;
  for (const code of codes) {
    if (!code.startsWith(`${prefix}-${year}-`)) continue;
    const value = Number.parseInt(code.slice(key.length + 1), 10);
    if (Number.isFinite(value) && value > highest) highest = value;
  }
  sequences.set(key, highest);
}

/**
 * A seeded pseudo-random generator.
 *
 * The demonstration dataset must be identical on every machine — a screenshot
 * in the documentation has to match what the reader sees. `Math.random()` would
 * make every reset a different company.
 */
export function createRandom(seed: number): () => number {
  let state = seed >>> 0 || 1;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    state >>>= 0;
    return state / 0xffffffff;
  };
}

export function pick<T>(random: () => number, items: readonly T[]): T {
  return items[Math.floor(random() * items.length) % items.length]!;
}

export function between(random: () => number, min: number, max: number): number {
  return Math.floor(random() * (max - min + 1)) + min;
}
